import React from 'react';
import { router } from '@inertiajs/react';
import { Users } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

// Per-agent enablement control shown in the worker-filtered integrations view
// (SZ-1561): the switch posts to the existing toggle_integration action.
export interface WorkerToggle {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

// Worker-scoped sub-account selection (SZ-1562): Google/Meta cards in the
// agent-filtered view open the account modals hosted by the index page.
export interface ManageAccountsAction {
  label: string;
  onClick: () => void;
}

interface IntegrationCardProps {
  name: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  connected: boolean;
  workerCount: number;
  // Ainda recebidas do Index (não usadas no card minimalista — detalhe fica na
  // página da integração).
  accountCount?: number | null;
  accountLabels?: string[];
  workerToggle?: WorkerToggle;
  manageAccounts?: ManageAccountsAction;
}

function isInteractiveChildEvent(event: React.KeyboardEvent<HTMLElement>): boolean {
  if (!(event.target instanceof Element)) return false
  if (event.target === event.currentTarget) return false

  return Boolean(event.target.closest('a, button, input, textarea, select, [role="switch"], [contenteditable="true"], [contenteditable=""]'))
}

/**
 * Card de integração do catálogo em grid (mantido em grid a pedido do fundador —
 * o ícone de marca dá personalidade). Minimalista: uma linha só (bolinha de
 * estado + ícone + nome + descrição), sem badge de texto. O ESTADO abre o card
 * pela esquerda (verde = conectada, cinza = não). À direita do nome fica o
 * mostrador enxuto de uso (ícone de usuário + número) no catálogo da org; no
 * painel do agente a contagem é indiferente, então o toggle por-agente ocupa
 * esse lugar, sem texto.
 */
export default function IntegrationCard({
  name,
  description,
  href,
  icon,
  connected,
  workerCount,
  workerToggle,
  manageAccounts,
}: IntegrationCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => router.visit(href)}
      onKeyDown={(event) => {
        if (isInteractiveChildEvent(event)) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          router.visit(href);
        }
      }}
      className="group flex h-full cursor-pointer items-start gap-3 rounded-xl border border-border bg-card p-4 transition-colors duration-150 hover:border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Estado como bolinha na BORDA ESQUERDA do card (verde = conectada,
          cinza = não): a varredura da grade acontece pela esquerda, então o
          status é a primeira coisa lida — antes era um detalhe perdido no
          canto direito. Alinhada ao meio do ícone de marca (48px). */}
      <span
        className={cn('mt-5 size-2 shrink-0 rounded-full', connected ? 'bg-success' : 'bg-muted-foreground/40')}
        title={connected ? 'Conectada' : 'Não conectada'}
        aria-label={connected ? 'Conectada' : 'Não conectada'}
      />
      <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-secondary [&_img]:size-9 [&_svg]:size-9">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate text-[14px] font-semibold text-foreground">{name}</h3>

          <div className="flex shrink-0 items-center gap-2">
            {workerToggle ? (
              // Painel do agente: toggle no lugar do contador, sem texto.
              // stopPropagation: o card navega no clique; o switch só alterna.
              <Switch
                checked={workerToggle.enabled}
                onCheckedChange={workerToggle.onToggle}
                onClick={(event) => event.stopPropagation()}
                aria-label={`Habilitar ${name} para o agente`}
              />
            ) : connected && workerCount > 0 ? (
              // Org: mostrador enxuto de uso — ícone de usuário + número.
              <span
                className="flex items-center gap-1 text-[11.5px] tabular-nums text-muted-foreground"
                title={`${workerCount} ${workerCount === 1 ? 'colaborador usa' : 'colaboradores usam'} esta integração`}
              >
                <Users className="size-3.5" strokeWidth={1.75} />
                {workerCount}
              </span>
            ) : null}
          </div>
        </div>

        <p className="mt-0.5 line-clamp-1 text-[13px] text-muted-foreground">{description}</p>

        {manageAccounts && (
          // stopPropagation: abre o modal de subcontas em vez de navegar.
          <div className="mt-2" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="text-[11.5px] text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
              onClick={manageAccounts.onClick}
            >
              {manageAccounts.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
